'use client';

import { useState } from 'react';
import { Laptop, LogOutIcon, Moon, MoreVerticalIcon, Sun, UserCircleIcon } from 'lucide-react';

import { Link } from '@/components/link';
import { useMeCtx } from '@/components/providers/v1-me-provider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';

export function NavUser() {
  const { me } = useMeCtx();

  const { isMobile } = useSidebar();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='text-foreground transition-colors data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <Avatar className='h-8 w-8 rounded-lg grayscale'>
                <AvatarImage src={me?.avatar_url ?? undefined} />
                <AvatarFallback className='rounded-lg border-1'>{me?.first_name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-medium'>
                  {me?.first_name} {me?.last_name}
                </span>
              </div>
              <MoreVerticalIcon className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] max-w-72 min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarImage src={me?.avatar_url ?? undefined} />
                  <AvatarFallback className='rounded-lg'>{me?.email?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className='w-full space-y-1 truncate'>
                  <div className='truncate text-sm leading-none font-medium'>
                    {me?.first_name} {me?.last_name}
                  </div>
                  <div className='truncate text-xs leading-none font-normal text-muted-foreground'>{me?.email}</div>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link href='/account'>
                  <UserCircleIcon />
                  Account
                </Link>
              </DropdownMenuItem>
              <DropdownMenuTheme />
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => {}}>
              <LogOutIcon />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

const themes = [
  {
    icon: Sun,
    value: 'light',
    label: 'Light',
  },
  {
    icon: Moon,
    value: 'dark',
    label: 'Dark',
  },
  {
    icon: Laptop,
    value: 'system',
    label: 'System',
  },
];

function DropdownMenuTheme() {
  const [theme, setTheme] = useState('light');
  const selectedTheme = themes.find((t) => t.value === theme);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {selectedTheme && <selectedTheme.icon className='mr-2 size-4' />} Theme
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          {themes.map(({ icon: Icon, value, label }) => {
            return (
              <DropdownMenuCheckboxItem key={value} checked={value === theme} onClick={() => setTheme(value)}>
                <Icon className='size-4' />
                {label}
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
